import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { Icon, IconProps } from './Icon';
import { legacyTextStyles } from './LegacyTextStyles';
import { cn } from './cn';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      legacyTextStyles.body({ color: 'secondary', size: 'm' }),
      'flex cursor-pointer select-none items-center gap-2 rounded-lg px-2 py-1.5 outline-none focus:bg-bg-tertiary focus:text-text-primary data-[state=open]:bg-bg-tertiary data-[state=open]:text-text-primary [&_svg]:pointer-events-none [&_svg]:shrink-0',
      inset && 'pl-8',
      className,
    )}
    {...props}
  >
    {children}
    <Icon className="ml-auto" name="ChevronRight" size="sm" />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      'z-50 min-w-[8rem] overflow-hidden rounded-xl border border-border-primary bg-bg-primary p-2 text-text-secondary shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className,
    )}
    {...props}
  />
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    usesPortal?: boolean;
  }
>(({ className, sideOffset = 4, usesPortal, ...props }, ref) => {
  const content = (
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 min-w-[8rem] overflow-hidden rounded-xl border border-border-primary bg-bg-primary p-2 text-text-secondary shadow-md',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  );

  if (usesPortal === false) {
    return content;
  }

  return <DropdownMenuPrimitive.Portal>{content}</DropdownMenuPrimitive.Portal>;
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      legacyTextStyles.body({ color: 'secondary', size: 'm' }),
      'relative flex cursor-pointer select-none items-center gap-2 rounded-lg px-2 py-1.5 outline-none transition-colors focus:bg-bg-tertiary focus:text-text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      legacyTextStyles.body({ color: 'secondary', size: 'm' }),
      'relative flex cursor-pointer select-none items-center rounded-lg py-1.5 pl-8 pr-2 outline-none transition-colors focus:bg-bg-tertiary focus:text-text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-1 flex size-5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon name="Check" size="xs" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      legacyTextStyles.body({ color: 'secondary', size: 'm' }),
      'relative flex cursor-pointer select-none items-center rounded-lg py-1.5 pl-8 pr-2 outline-none transition-colors focus:bg-bg-tertiary focus:text-text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex size-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon className="size-2 fill-current" name="Circle" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      legacyTextStyles.body({ color: 'supporting', size: 'm' }),
      'px-2 py-1.5',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-2 my-2 h-px bg-border-primary', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn('ml-auto text-xs tracking-widest opacity-60', className)}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

// Custom Components

function DropdownMenuHeading({ children }: { children: React.ReactNode }) {
  return (
    <DropdownMenuPrimitive.Label
      className={legacyTextStyles.body({
        color: 'supporting',
        size: 's',
        weight: 'semibold',
        className: 'px-2 py-1.5 uppercase',
      })}
    >
      {children}
    </DropdownMenuPrimitive.Label>
  );
}

function DropdownMenuAnchorItem({
  href,
  icon,
  rel = 'noreferrer',
  target = '_blank',
  text,
}: {
  href: string;
  icon?: {
    name: IconProps['name'];
  };
  rel?: string;
  target?: string;
  text: string;
}) {
  return (
    <DropdownMenuItem asChild>
      <a href={href} rel={rel} target={target}>
        {icon && <Icon name={icon.name} size="xs" />}
        {text}
      </a>
    </DropdownMenuItem>
  );
}

function DropdownMenuCustomItem({ children }: { children: React.ReactNode }) {
  return (
    <div className="pointer-events-none relative flex cursor-pointer select-none items-center gap-2 px-2 py-1.5 outline-none">
      {children}
    </div>
  );
}

function DropdownMenuLinkItem({
  icon,
  onClick,
  text,
  to,
}: {
  icon?: {
    name: IconProps['name'];
  };
  onClick?: () => void;
  text: string;
  to: string;
}) {
  return (
    <DropdownMenuItem asChild>
      <Link onClick={onClick} to={to}>
        {icon && <Icon name={icon.name} size="xs" />}
        {text}
      </Link>
    </DropdownMenuItem>
  );
}

function DropdownMenuSelectableItem({
  currentValue,
  onClick,
  text,
  value,
}: {
  currentValue: string;
  onClick: (value: string) => void;
  text: string;
  value: string;
}) {
  const selected = currentValue === value;

  return (
    <DropdownMenuCheckboxItem
      checked={selected}
      disabled={selected}
      onClick={() => {
        onClick(value);
      }}
    >
      {text}
    </DropdownMenuCheckboxItem>
  );
}

/*
The following components are currently not used, so they are not exported:
  - DropdownMenuCheckboxItem
  - DropdownMenuGroup (import from DropdownMenuPrimitive)
  - DropdownMenuPortal (import from DropdownMenuPrimitive)
  - DropdownMenuRadioGroup (import from DropdownMenuPrimitive)
  - DropdownMenuRadioItem
  - DropdownMenuShortcut
*/

export {
  DropdownMenu,
  DropdownMenuAnchorItem,
  DropdownMenuContent,
  DropdownMenuCustomItem,
  DropdownMenuHeading,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuLinkItem,
  DropdownMenuSelectableItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
